import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox, FileText, Users, LogOut, Plus, Trash2, Pencil, ExternalLink, X,
  Mail, Phone, CheckCircle2, Star, RefreshCw, FileDown, ScrollText, Settings, MapPin, Save, LayoutGrid, Wallet, Send, IndianRupee, Truck,
} from "lucide-react";
import { AdminCashPickups } from "../components/cashpickup/AdminCashPickups";
import PhotonLogo from "../components/PhotonLogo";
import BlogEditor from "../components/BlogEditor";
import PolicyEditor from "../components/PolicyEditor";
import ServiceEditor from "../components/ServiceEditor";
import { useToast } from "../hooks/use-toast";
import {
  adminMe, getStats, adminEnquiries, setEnquiryStatus, deleteEnquiry,
  adminBlogList, adminBlogDelete, adminSignups, setSignupStatus, deleteSignup, fileUrl,
  adminPolicies, adminPolicyDelete,
  adminGetSettings, adminUpdateSettings,
  adminGetBranding, adminUpdateBranding,
  adminServices, adminServiceDelete,
  adminRetailers, adminCreateRetailer, adminWalletLoad, adminWalletTxns, adminPayouts, adminUpdatePayout, getPayoutConfig, updatePayoutConfig, adminProviderBalance, adminPayoutStatusCheck,
} from "../api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) => { try { return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };

const TABS = [
  { key: "overview", label: "Overview", icon: RefreshCw },
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "blog", label: "Blog", icon: FileText },
  { key: "signups", label: "Merchant Signups", icon: Users },
  { key: "wallets", label: "Retailer Wallets", icon: Wallet },
  { key: "payouts", label: "Payouts", icon: Send },
  { key: "pickups", label: "Cash Pickups", icon: Truck },
  { key: "services", label: "Services", icon: LayoutGrid },
  { key: "policies", label: "Policies", icon: ScrollText },
  { key: "settings", label: "Contact Info", icon: Settings },
  { key: "branding", label: "Branding", icon: Star },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [posts, setPosts] = useState([]);
  const [signups, setSignups] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // {id} or 'new'
  const [editingPolicy, setEditingPolicy] = useState(null); // id or 'new'
  const [editingService, setEditingService] = useState(null); // id or 'new'
  const [selectedSignup, setSelectedSignup] = useState(null);
  const [settingsForm, setSettingsForm] = useState({ email: "", phone: "", address: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [retailers, setRetailers] = useState([]);
  const [allPayouts, setAllPayouts] = useState([]);
  const [payoutCfg, setPayoutCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [providerBalance, setProviderBalance] = useState(null);
  const [showAddRetailer, setShowAddRetailer] = useState(false);
  const [newRetailer, setNewRetailer] = useState({ full_name: "", business_name: "", phone: "", email: "", password: "" });
  const [creatingRetailer, setCreatingRetailer] = useState(false);
  const [loadTarget, setLoadTarget] = useState(null);
  const [loadAmount, setLoadAmount] = useState("");
  const [loadRemarks, setLoadRemarks] = useState("");
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [settleConfirm, setSettleConfirm] = useState(null);
  const [statusResult, setStatusResult] = useState(null);
  const [statement, setStatement] = useState(null); // {retailer, txns}
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [branding, setBranding] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);

  const logout = () => { localStorage.removeItem("pp_admin_token"); navigate("/admin/login"); };

  const loadAll = useCallback(() => {
    getStats().then(setStats).catch(() => {});
    adminEnquiries().then(setEnquiries).catch(() => {});
    adminBlogList().then(setPosts).catch(() => {});
    adminSignups().then(setSignups).catch(() => {});
    adminPolicies().then(setPolicies).catch(() => {});
    adminServices().then(setServices).catch(() => {});
    adminRetailers().then(setRetailers).catch(() => {});
    adminPayouts().then(setAllPayouts).catch(() => {});
    getPayoutConfig().then(setPayoutCfg).catch(() => {});
    adminProviderBalance().then(setProviderBalance).catch(() => setProviderBalance(null));
    adminGetSettings().then((s) => setSettingsForm({ email: s.email || "", phone: s.phone || "", address: s.address || "" })).catch(() => {});
    adminGetBranding().then(setBranding).catch(() => {});
  }, []);

  const saveSettings = async () => {
    if (!settingsForm.email || !settingsForm.phone || !settingsForm.address) {
      toast({ title: "Please fill in email, phone and address." });
      return;
    }
    setSavingSettings(true);
    try {
      await adminUpdateSettings(settingsForm);
      toast({ title: "Contact info updated", description: "Changes are now live on the website." });
    } catch {
      toast({ title: "Failed to save", description: "Please try again." });
    } finally {
      setSavingSettings(false);
    }
  };

  const loadWallet = (r) => {
    setLoadTarget(r);
    setLoadAmount("");
    setLoadRemarks("");
  };

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast({ title: "Logo too large", description: "Please use an image under 1MB." }); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setBranding((b) => ({ ...b, logo: dataUrl }));
      // auto-extract colors from the logo
      const img = new Image();
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          const w = (c.width = 48), h = (c.height = 48);
          const ctx = c.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          const d = ctx.getImageData(0, 0, w, h).data;
          const counts = {};
          for (let i = 0; i < d.length; i += 4) {
            const a = d[i + 3]; if (a < 200) continue;
            const r = d[i], g = d[i + 1], bl = d[i + 2];
            const mx = Math.max(r, g, bl), mn = Math.min(r, g, bl);
            if (mx > 242 && mn > 242) continue; if (mx < 18) continue;
            const key = `${Math.round(r / 24) * 24},${Math.round(g / 24) * 24},${Math.round(bl / 24) * 24}`;
            counts[key] = (counts[key] || 0) + 1 + (mx - mn) / 40;
          }
          const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
          const toHex = (s) => "#" + s.split(",").map((x) => Math.min(255, Number(x)).toString(16).padStart(2, "0")).join("");
          if (sorted.length) {
            const accent = toHex(sorted[0][0]);
            const dark = sorted.find(([k]) => { const [r, g, bl] = k.split(",").map(Number); return (r + g + bl) / 3 < 120; });
            setBranding((b) => ({ ...b, accent_color: accent, accent2_color: accent, primary_color: dark ? toHex(dark[0]) : b.primary_color }));
            toast({ title: "Colors extracted from logo 🎨", description: "You can fine-tune them below before saving." });
          }
        } catch { /* ignore */ }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const saveBranding = async () => {
    if (!branding.company_name) { toast({ title: "Company name is required" }); return; }
    setSavingBrand(true);
    try {
      await adminUpdateBranding(branding);
      toast({ title: "Branding saved 🎉", description: "Refresh the site to see it everywhere." });
      // apply immediately to CSS vars + title
      const r = document.documentElement.style;
      r.setProperty("--pp-navy", branding.primary_color);
      r.setProperty("--pp-orange", branding.accent_color);
      r.setProperty("--pp-orange-light", branding.accent2_color);
      document.title = branding.company_name;
    } catch (err) {
      toast({ title: "Save failed", description: err.response?.data?.detail || "Try again" });
    } finally {
      setSavingBrand(false);
    }
  };

  const onLogoUploadDone = () => {};

  const openStatement = async (r) => {
    setStatement({ retailer: r, txns: [] });
    setLoadingStatement(true);
    try {
      const txns = await adminWalletTxns(r.id);
      setStatement({ retailer: r, txns });
    } catch {
      toast({ title: "Could not load statement" });
    } finally {
      setLoadingStatement(false);
    }
  };

  const downloadStatementCSV = () => {
    if (!statement) return;
    const rows = [["Date", "Description", "Type", "Direction", "Amount", "Balance"]];
    statement.txns.forEach((t) => rows.push([new Date(t.created_at).toLocaleString("en-IN"), t.reason, t.type, t.direction, t.amount, t.balance_after]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `statement_${(statement.retailer.full_name || "retailer").replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitLoadWallet = async () => {
    const amount = parseFloat(loadAmount);
    if (!amount || amount <= 0) { toast({ title: "Enter a valid amount" }); return; }
    setLoadingWallet(true);
    try {
      const res = await adminWalletLoad({ retailer_id: loadTarget.id, amount, remarks: loadRemarks || "Wallet load by admin" });
      toast({ title: "Wallet loaded 🎉", description: `${loadTarget.full_name}: new balance ${inr(res.balance)}` });
      setLoadTarget(null);
      loadAll();
    } catch (err) {
      toast({ title: "Load failed", description: err.response?.data?.detail || "Try again" });
    } finally {
      setLoadingWallet(false);
    }
  };

  const settlePayout = (p, status) => setSettleConfirm({ p, status });

  const doSettle = async () => {
    const { p, status } = settleConfirm;
    try {
      await adminUpdatePayout(p.id, { status });
      toast({ title: `Payout marked ${status}` });
      setSettleConfirm(null);
      loadAll();
    } catch (err) {
      toast({ title: "Update failed", description: err.response?.data?.detail || "Try again" });
    }
  };

  const createRetailer = async () => {
    if (!newRetailer.full_name || !newRetailer.phone || !newRetailer.password) {
      toast({ title: "Name, mobile number and password are required" });
      return;
    }
    setCreatingRetailer(true);
    try {
      await adminCreateRetailer(newRetailer);
      toast({ title: "Retailer created 🎉", description: `${newRetailer.full_name} can now log in with their mobile number.` });
      setShowAddRetailer(false);
      setNewRetailer({ full_name: "", business_name: "", phone: "", email: "", password: "" });
      loadAll();
    } catch (err) {
      toast({ title: "Could not create retailer", description: err.response?.data?.detail || "Try again" });
    } finally {
      setCreatingRetailer(false);
    }
  };

  const checkPayoutStatus = async (p) => {
    try {
      const data = await adminPayoutStatusCheck(p.id);
      setStatusResult({ payout: p, data });
    } catch (err) {
      toast({ title: "Status check failed", description: err.response?.data?.detail || "Try again" });
    }
  };

  const savePayoutCfg = async () => {
    setSavingCfg(true);
    try {
      await updatePayoutConfig({
        charge_flat: parseFloat(payoutCfg.charge_flat) || 0,
        charge_percent: parseFloat(payoutCfg.charge_percent) || 0,
        commission_flat: parseFloat(payoutCfg.commission_flat) || 0,
        commission_percent: parseFloat(payoutCfg.commission_percent) || 0,
        min_amount: parseFloat(payoutCfg.min_amount) || 1,
        max_amount: parseFloat(payoutCfg.max_amount) || 200000,
      });
      toast({ title: "Payout fee settings saved" });
    } catch {
      toast({ title: "Save failed" });
    } finally {
      setSavingCfg(false);
    }
  };

  useEffect(() => {
    adminMe().then(loadAll).catch(() => navigate("/admin/login"));
  }, []);

  const handleDeletePost = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await adminBlogDelete(id);
    toast({ title: "Article deleted" });
    loadAll();
  };

  const StatCard = ({ label, value, sub, icon: Icon }) => (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#64748B] font-medium">{label}</span>
        <Icon size={18} className="text-[#0EA5E9]" />
      </div>
      <div className="text-3xl font-extrabold text-[#0D1B2A] mt-2">{value}</div>
      {sub && <div className="text-xs text-[#06B6D4] font-semibold mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f6f9fd] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-100 min-h-screen sticky top-0 hidden md:flex flex-col">
        <div className="p-5 border-b border-slate-100"><PhotonLogo variant="dark" /></div>
        <nav className="p-3 flex-1">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold mb-1 transition-colors ${tab === t.key ? "bg-[#0D1B2A] text-white" : "text-[#1E293B] hover:bg-[#F0FDFA]"}`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50">
            <LogOut size={17} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Topbar */}
        <div className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <h1 className="text-xl font-extrabold text-[#0D1B2A] capitalize">{TABS.find((t) => t.key === tab)?.label || tab}</h1>
          <div className="flex items-center gap-2">
            {tab !== "pickups" && <button onClick={loadAll} className="flex items-center gap-1.5 text-sm font-semibold text-[#0EA5E9] px-3 py-2 rounded-lg hover:bg-[#F0FDFA]"><RefreshCw size={15} /> Refresh</button>}
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm font-semibold text-[#0D1B2A] px-3 py-2 rounded-lg hover:bg-[#F0FDFA]"><ExternalLink size={15} /> View Site</a>
            <button onClick={logout} className="md:hidden text-red-500 p-2"><LogOut size={18} /></button>
          </div>
        </div>

        <div className="p-6">
          {/* Mobile tabs */}
          <div className="md:hidden flex gap-2 overflow-x-auto mb-5">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${tab === t.key ? "bg-[#0D1B2A] text-white" : "bg-white text-[#1E293B]"}`}>{t.label}</button>
            ))}
          </div>

          {/* Overview */}
          {tab === "overview" && stats && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <StatCard label="Total Enquiries" value={stats.enquiries} sub={`${stats.enquiries_new} new`} icon={Inbox} />
              <StatCard label="Blog Articles" value={stats.blog} sub={`${stats.blog_published} published`} icon={FileText} />
              <StatCard label="Merchant Signups" value={stats.signups} sub={`${stats.signups_pending} pending`} icon={Users} />
              <StatCard label="Services" value={stats.services} icon={LayoutGrid} />
            </div>
          )}

          {/* Enquiries */}
          {tab === "enquiries" && (
            <div className="space-y-3">
              {enquiries.length === 0 && <div className="text-[#64748B] text-center py-16">No enquiries yet.</div>}
              {enquiries.map((e) => (
                <div key={e.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#0D1B2A]">{e.name}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${e.status === "new" ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"}`}>{e.status}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#475569] mt-1">
                        <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-[#0EA5E9]"><Mail size={13} /> {e.email}</a>
                        {e.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {e.phone}</span>}
                      </div>
                      {e.message && <p className="text-sm text-[#1E293B] mt-2">{e.message}</p>}
                      <div className="text-xs text-[#94A3B8] mt-2">{fmt(e.created_at)}</div>
                    </div>
                    <div className="flex gap-2">
                      {e.status === "new" && (
                        <button onClick={async () => { await setEnquiryStatus(e.id, "handled"); loadAll(); }} className="flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-3 py-2 rounded-lg"><CheckCircle2 size={14} /> Mark handled</button>
                      )}
                      <button onClick={async () => { if (window.confirm("Delete?")) { await deleteEnquiry(e.id); loadAll(); } }} className="text-red-500 p-2 rounded-lg hover:bg-red-50"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Blog */}
          {tab === "blog" && (
            <div>
              <div className="flex justify-end mb-4">
                <button onClick={() => setEditing("new")} className="flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                  <Plus size={16} /> New Article
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {posts.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                    <div className="h-36 bg-slate-100 overflow-hidden relative">
                      {p.cover_image && <img src={fileUrl(p.cover_image)} alt="" className="w-full h-full object-cover" />}
                      {p.featured && <span className="absolute top-2 left-2 bg-[#06B6D4] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} /> Featured</span>}
                      <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${p.published ? "bg-green-500 text-white" : "bg-slate-400 text-white"}`}>{p.published ? "Live" : "Draft"}</span>
                    </div>
                    <div className="p-4">
                      <span className="text-[11px] font-semibold text-[#06B6D4]">{p.category}</span>
                      <h3 className="font-bold text-[#0D1B2A] leading-snug line-clamp-2 mt-1">{p.title}</h3>
                      <div className="flex items-center gap-2 mt-3">
                        <button onClick={() => setEditing(p.id)} className="flex items-center gap-1 text-xs font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-1.5 rounded-lg"><Pencil size={13} /> Edit</button>
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-semibold text-[#0D1B2A] px-3 py-1.5 rounded-lg hover:bg-slate-100"><ExternalLink size={13} /> View</a>
                        <button onClick={() => handleDeletePost(p.id)} className="ml-auto text-red-500 p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Signups */}
          {tab === "signups" && (
            <div className="space-y-3">
              {signups.length === 0 && <div className="text-[#64748B] text-center py-16">No merchant signups yet.</div>}
              {signups.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#0D1B2A]">{s.full_name}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.status === "pending" ? "bg-orange-100 text-orange-600" : s.status === "approved" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{s.status}</span>
                      </div>
                      <div className="text-sm text-[#475569] mt-1">{s.business_name} · {s.business_type}</div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#475569] mt-1">
                        <span className="flex items-center gap-1.5"><Mail size={13} /> {s.email}</span>
                        <span className="flex items-center gap-1.5"><Phone size={13} /> {s.phone}</span>
                      </div>
                      <div className="text-xs text-[#94A3B8] mt-2">{fmt(s.created_at)} · {(s.documents || []).length} document(s)</div>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <button onClick={() => setSelectedSignup(s)} className="text-xs font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-2 rounded-lg">View details</button>
                      <div className="flex gap-2">
                        <button onClick={async () => { await setSignupStatus(s.id, "approved"); loadAll(); }} className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">Approve</button>
                        <button onClick={async () => { await setSignupStatus(s.id, "rejected"); loadAll(); }} className="text-xs font-semibold text-red-500 bg-red-50 px-3 py-1.5 rounded-lg">Reject</button>
                        <button onClick={async () => { if (window.confirm("Delete?")) { await deleteSignup(s.id); loadAll(); } }} className="text-red-500 p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Retailer Wallets */}
          {tab === "wallets" && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button onClick={() => setShowAddRetailer(true)} data-testid="add-retailer-button" className="flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#0D1B2A,#0EA5E9)" }}>
                  <Plus size={16} /> Add Retailer
                </button>
              </div>
              {retailers.length === 0 && <div className="text-[#64748B] text-center py-16">No approved retailers yet. Add one, or approve a merchant signup.</div>}
              {retailers.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center justify-between gap-4 flex-wrap" data-testid={`retailer-row-${r.id}`}>
                  <div>
                    <div className="font-bold text-[#0D1B2A]">{r.full_name} <span className="text-sm font-normal text-[#64748B]">· {r.business_name}</span></div>
                    <div className="flex flex-wrap gap-4 text-sm text-[#475569] mt-1">
                      <span className="flex items-center gap-1.5"><Mail size={13} /> {r.email}</span>
                      <span className="flex items-center gap-1.5"><Phone size={13} /> {r.phone}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-[11px] uppercase text-[#94A3B8] font-semibold">Balance</div>
                      <div className="text-xl font-extrabold text-[#0D1B2A]" data-testid={`retailer-balance-${r.id}`}>{inr(r.wallet_balance)}</div>
                    </div>
                    <button onClick={() => openStatement(r)} data-testid={`statement-${r.id}`} className="flex items-center gap-1.5 text-[#0D1B2A] font-semibold text-sm px-4 py-2.5 rounded-full border border-slate-200 hover:bg-slate-50">
                      <ScrollText size={15} /> Statement
                    </button>
                    <button onClick={() => loadWallet(r)} data-testid={`load-wallet-${r.id}`} className="flex items-center gap-1.5 text-white font-semibold text-sm px-4 py-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                      <IndianRupee size={15} /> Load
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Payouts */}
          {tab === "payouts" && (
            <div className="space-y-5">
              {/* Live provider balance */}
              <div className="rounded-2xl p-5 text-white shadow-lg flex items-center justify-between flex-wrap gap-4" style={{ background: "linear-gradient(135deg,#0D1B2A,#0EA5E9)" }} data-testid="provider-balance-card">
                <div>
                  <div className="flex items-center gap-2 text-white/80 text-sm font-medium"><Wallet size={16} /> PhotonPE Master Wallet (live)</div>
                  <div className="text-3xl font-extrabold mt-1">{providerBalance ? inr(providerBalance.balance) : "—"}</div>
                  <div className="text-xs text-white/60 mt-1">This is the aggregator account that funds real payouts.</div>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${providerBalance?.live ? "bg-green-400/20 text-green-100" : "bg-orange-400/20 text-orange-100"}`}>
                  {providerBalance?.live ? "LIVE PAYOUTS ON" : "LIVE PAYOUTS OFF (manual settle)"}
                </span>
              </div>

              {payoutCfg && (
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                  <h3 className="font-bold text-[#0D1B2A] mb-3">Payout Fee & Commission Settings</h3>
                  <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[["charge_flat", "Fee (flat ₹)"], ["charge_percent", "Fee (%)"], ["commission_flat", "Commission (flat ₹)"], ["commission_percent", "Commission (%)"], ["min_amount", "Min ₹"], ["max_amount", "Max ₹"]].map(([k, lbl]) => (
                      <div key={k}>
                        <label className="text-[11px] font-semibold text-[#1E293B] uppercase">{lbl}</label>
                        <input type="number" step="0.01" data-testid={`payout-cfg-${k}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0EA5E9]" value={payoutCfg[k]} onChange={(e) => setPayoutCfg({ ...payoutCfg, [k]: e.target.value })} />
                      </div>
                    ))}
                  </div>
                  <button onClick={savePayoutCfg} disabled={savingCfg} data-testid="save-payout-cfg" className="mt-4 flex items-center gap-2 text-white font-semibold text-sm px-5 py-2.5 rounded-full disabled:opacity-60" style={{ background: "linear-gradient(90deg,#0D1B2A,#0EA5E9)" }}>
                    <Save size={15} /> {savingCfg ? "Saving…" : "Save Settings"}
                  </button>
                </div>
              )}

              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                <h3 className="font-bold text-[#0D1B2A] mb-4">All Payouts</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-payouts-table">
                    <thead><tr className="text-left text-[#94A3B8] text-xs uppercase border-b border-slate-100">
                      <th className="py-2 pr-3">Beneficiary</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Fee</th><th className="py-2 pr-3">Mode</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Date</th><th className="py-2">Action</th>
                    </tr></thead>
                    <tbody>
                      {allPayouts.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-[#64748B]">No payouts yet.</td></tr>}
                      {allPayouts.map((p) => (
                        <tr key={p.id} className="border-b border-slate-50" data-testid={`admin-payout-row-${p.id}`}>
                          <td className="py-3 pr-3"><div className="font-semibold text-[#0D1B2A]">{p.beneficiary_name}</div><div className="text-xs text-[#94A3B8]">{p.account_number} · {p.ifsc}</div></td>
                          <td className="py-3 pr-3 font-semibold text-[#0D1B2A]">{inr(p.amount)}</td>
                          <td className="py-3 pr-3 text-[#64748B]">{inr(p.charge)}</td>
                          <td className="py-3 pr-3">{p.mode}</td>
                          <td className="py-3 pr-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.status === "success" ? "bg-green-100 text-green-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}>{p.status}</span></td>
                          <td className="py-3 pr-3 text-xs text-[#94A3B8]">{fmt(p.created_at)}</td>
                          <td className="py-3">
                            {p.status === "processing" ? (
                              <div className="flex gap-2 flex-wrap">
                                <button onClick={() => checkPayoutStatus(p)} data-testid={`payout-check-${p.id}`} className="text-xs font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-1.5 rounded-lg">Check</button>
                                <button onClick={() => settlePayout(p, "success")} data-testid={`payout-success-${p.id}`} className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">Success</button>
                                <button onClick={() => settlePayout(p, "failed")} data-testid={`payout-failed-${p.id}`} className="text-xs font-semibold text-red-500 bg-red-50 px-3 py-1.5 rounded-lg">Fail</button>
                              </div>
                            ) : <span className="text-xs text-[#94A3B8]">Settled</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "pickups" && <AdminCashPickups />}

          {/* Services */}
          {tab === "services" && (
            <div>
              <div className="flex justify-end mb-4">
                <button onClick={() => setEditingService("new")} data-testid="new-service-button" className="flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                  <Plus size={16} /> New Service
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {services.map((s) => (
                  <div key={s.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`service-card-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="text-[11px] font-semibold text-[#06B6D4]">{s.group}</span>
                        <h3 className="font-bold text-[#0D1B2A] leading-snug mt-0.5">{s.label}</h3>
                        <div className="text-xs text-[#94A3B8] mt-1 line-clamp-1">{s.title} {s.title_accent} {s.title_tail}</div>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${s.published ? "bg-green-500 text-white" : "bg-slate-400 text-white"}`}>{s.published ? "Live" : "Hidden"}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <button onClick={() => setEditingService(s.id)} className="flex items-center gap-1 text-xs font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-1.5 rounded-lg"><Pencil size={13} /> Edit</button>
                      <a href="/#services" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-semibold text-[#0D1B2A] px-3 py-1.5 rounded-lg hover:bg-slate-100"><ExternalLink size={13} /> View</a>
                      <button onClick={async () => { if (window.confirm("Delete this service?")) { await adminServiceDelete(s.id); loadAll(); } }} className="ml-auto text-red-500 p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-service-${s.id}`}><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Policies */}
          {tab === "policies" && (
            <div>
              <div className="flex justify-end mb-4">
                <button onClick={() => setEditingPolicy("new")} className="flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                  <Plus size={16} /> New Policy
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {policies.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-[#0D1B2A]">{p.title}</h3>
                        <div className="text-xs text-[#94A3B8] mt-1">/{p.slug} · {p.updated_label}</div>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.published ? "bg-green-500 text-white" : "bg-slate-400 text-white"}`}>{p.published ? "Live" : "Hidden"}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <button onClick={() => setEditingPolicy(p.id)} className="flex items-center gap-1 text-xs font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-1.5 rounded-lg"><Pencil size={13} /> Edit</button>
                      <a href={`/policies/${p.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-semibold text-[#0D1B2A] px-3 py-1.5 rounded-lg hover:bg-slate-100"><ExternalLink size={13} /> View</a>
                      <button onClick={async () => { if (window.confirm("Delete this policy?")) { await adminPolicyDelete(p.id); loadAll(); } }} className="ml-auto text-red-500 p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Branding / White-label */}
          {tab === "branding" && branding && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-5">
                <div>
                  <h2 className="text-lg font-bold text-[#0D1B2A]">White-label Branding</h2>
                  <p className="text-sm text-[#64748B] mt-1">Set your company logo, name and colors — the whole site & portal rebrand instantly.</p>
                </div>

                {/* Logo */}
                <div>
                  <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Company Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-xl border border-slate-200 bg-[#f6f9fd] flex items-center justify-center overflow-hidden">
                      {branding.logo ? <img src={branding.logo} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-[#94A3B8]">No logo</span>}
                    </div>
                    <div>
                      <input type="file" accept="image/*" onChange={onLogoUpload} data-testid="branding-logo-input" className="text-sm" />
                      <p className="text-xs text-[#94A3B8] mt-1">PNG/JPG, under 1MB. Colors auto-extract from the logo.</p>
                      {branding.logo && <button onClick={() => setBranding({ ...branding, logo: "" })} className="text-xs text-red-500 font-semibold mt-1">Remove logo</button>}
                    </div>
                  </div>
                </div>

                {/* Name + tagline */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Company Name</label>
                    <input data-testid="branding-name-input" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#0EA5E9]" value={branding.company_name} onChange={(e) => setBranding({ ...branding, company_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Tagline</label>
                    <input className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#0EA5E9]" value={branding.tagline} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} />
                  </div>
                </div>

                {/* Wordmark split (used when no logo image) */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Wordmark — main part</label>
                    <input className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#0EA5E9]" placeholder="PHOTON" value={branding.name_primary} onChange={(e) => setBranding({ ...branding, name_primary: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Wordmark — accent part</label>
                    <input className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#0EA5E9]" placeholder="PE" value={branding.name_accent} onChange={(e) => setBranding({ ...branding, name_accent: e.target.value })} />
                  </div>
                </div>

                {/* Colors */}
                <div>
                  <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Theme Colors</label>
                  <div className="flex flex-wrap gap-5">
                    {[["primary_color", "Primary (dark)"], ["accent_color", "Accent"], ["accent2_color", "Accent 2"]].map(([k, lbl]) => (
                      <div key={k} className="flex items-center gap-2">
                        <input type="color" data-testid={`branding-${k}`} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" value={branding[k]} onChange={(e) => setBranding({ ...branding, [k]: e.target.value })} />
                        <div><div className="text-xs font-semibold text-[#1E293B]">{lbl}</div><div className="text-[11px] text-[#94A3B8]">{branding[k]}</div></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live preview */}
                <div className="rounded-xl p-5 border border-slate-100" style={{ background: branding.primary_color }}>
                  <div className="flex items-center gap-2.5">
                    {branding.logo ? <img src={branding.logo} alt="logo" className="h-10 w-auto object-contain" /> : (
                      <div className="flex items-baseline">
                        <span className="text-xl font-extrabold text-white">{branding.name_primary || branding.company_name}</span>
                        <span className="text-xl font-extrabold" style={{ color: branding.accent_color }}>{branding.name_accent}</span>
                      </div>
                    )}
                    <span className="ml-auto text-white text-sm font-semibold px-4 py-2 rounded-full" style={{ background: `linear-gradient(90deg, ${branding.accent_color}, ${branding.accent2_color})` }}>Sample Button</span>
                  </div>
                </div>

                <button onClick={saveBranding} disabled={savingBrand} data-testid="branding-save-button" className="flex items-center gap-2 text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" style={{ background: `linear-gradient(90deg, ${branding.accent_color}, ${branding.accent2_color})` }}>
                  <Save size={16} /> {savingBrand ? "Saving…" : "Save Branding"}
                </button>
              </div>
            </div>
          )}


          {/* Contact Info / Settings */}
          {tab === "settings" && (
            <div className="max-w-xl">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h2 className="text-lg font-bold text-[#0D1B2A]">Website Contact Info</h2>
                <p className="text-sm text-[#64748B] mt-1">These details appear in the footer, contact section and policy pages across the live site.</p>
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-[#1E293B] uppercase flex items-center gap-1.5"><Mail size={13} /> Email Address</label>
                    <input
                      data-testid="settings-email-input"
                      className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0f2540] outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition"
                      value={settingsForm.email}
                      onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
                      placeholder="info@photonpe.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#1E293B] uppercase flex items-center gap-1.5"><Phone size={13} /> Phone Number</label>
                    <input
                      data-testid="settings-phone-input"
                      className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0f2540] outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition"
                      value={settingsForm.phone}
                      onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })}
                      placeholder="+91 9311914818"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#1E293B] uppercase flex items-center gap-1.5"><MapPin size={13} /> Address</label>
                    <input
                      data-testid="settings-address-input"
                      className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0f2540] outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition"
                      value={settingsForm.address}
                      onChange={(e) => setSettingsForm({ ...settingsForm, address: e.target.value })}
                      placeholder="Office address"
                    />
                  </div>
                  <button
                    data-testid="settings-save-button"
                    onClick={saveSettings}
                    disabled={savingSettings}
                    className="flex items-center gap-2 text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60"
                    style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}
                  >
                    <Save size={16} /> {savingSettings ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Blog editor modal */}
      {editing && (
        <BlogEditor postId={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadAll(); }} />
      )}

      {/* Policy editor modal */}
      {editingPolicy && (
        <PolicyEditor policyId={editingPolicy === "new" ? null : editingPolicy} onClose={() => setEditingPolicy(null)} onSaved={() => { setEditingPolicy(null); loadAll(); }} />
      )}

      {/* Service editor modal */}
      {editingService && (
        <ServiceEditor serviceId={editingService === "new" ? null : editingService} onClose={() => setEditingService(null)} onSaved={() => { setEditingService(null); loadAll(); }} />
      )}

      {/* Settle payout confirm modal */}
      {settleConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="settle-confirm-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <h2 className="text-lg font-bold text-[#0D1B2A]">Confirm Payout {settleConfirm.status === "success" ? "Success" : "Failure"}</h2>
            <p className="text-sm text-[#475569] mt-2">
              Mark payout to <b>{settleConfirm.p.beneficiary_name}</b> ({inr(settleConfirm.p.amount)}) as <b>{settleConfirm.status}</b>?
              {settleConfirm.status === "failed" && " Amount + fee will be refunded to the retailer's wallet."}
            </p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setSettleConfirm(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 font-semibold text-[#1E293B]">Cancel</button>
              <button onClick={doSettle} data-testid="settle-confirm-button" className={`flex-1 py-2.5 rounded-xl text-white font-semibold ${settleConfirm.status === "success" ? "bg-green-600" : "bg-red-500"}`}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Payout provider status modal */}
      {statusResult && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="payout-status-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-[#0D1B2A]">Provider Status — {statusResult.payout.beneficiary_name}</h2>
              <button onClick={() => setStatusResult(null)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
            </div>
            <pre className="p-6 text-xs text-[#1E293B] overflow-auto max-h-[60vh] whitespace-pre-wrap">{JSON.stringify(statusResult.data, null, 2)}</pre>
          </div>
        </div>
      )}

      {/* Retailer statement modal */}
      {statement && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="statement-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-[#0D1B2A]">Statement — {statement.retailer.full_name}</h2>
                <div className="text-xs text-[#64748B]">{statement.retailer.business_name} · Balance {inr(statement.retailer.wallet_balance)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={downloadStatementCSV} data-testid="statement-download" className="flex items-center gap-1.5 text-sm font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-2 rounded-lg"><FileDown size={14} /> CSV</button>
                <button onClick={() => setStatement(null)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 overflow-auto">
              {loadingStatement ? (
                <div className="py-12 text-center text-[#64748B]">Loading…</div>
              ) : statement.txns.length === 0 ? (
                <div className="py-12 text-center text-[#64748B]">No transactions yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="statement-table">
                  <thead><tr className="text-left text-[#94A3B8] text-xs uppercase border-b border-slate-100">
                    <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Description</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Amount</th><th className="py-2">Balance</th>
                  </tr></thead>
                  <tbody>
                    {statement.txns.map((t) => (
                      <tr key={t.id} className="border-b border-slate-50">
                        <td className="py-2.5 pr-3 text-xs text-[#94A3B8]">{new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                        <td className="py-2.5 pr-3 text-[#1E293B]">{t.reason}</td>
                        <td className="py-2.5 pr-3 text-xs uppercase text-[#94A3B8]">{t.type}</td>
                        <td className={`py-2.5 pr-3 font-semibold ${t.direction === "credit" ? "text-green-600" : "text-red-500"}`}>{t.direction === "credit" ? "+" : "−"}{inr(t.amount)}</td>
                        <td className="py-2.5 text-[#0D1B2A]">{inr(t.balance_after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Load Wallet modal */}
      {loadTarget && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="load-wallet-modal">          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-[#0D1B2A]">Load Wallet</h2>
              <button onClick={() => setLoadTarget(null)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F0FDFA] rounded-xl px-4 py-3">
                <div className="font-bold text-[#0D1B2A]">{loadTarget.full_name}</div>
                <div className="text-xs text-[#475569]">{loadTarget.business_name} · Current: {inr(loadTarget.wallet_balance)}</div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Amount (₹) *</label>
                <input
                  data-testid="load-wallet-amount"
                  type="number"
                  autoFocus
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20"
                  placeholder="Enter amount to add"
                  value={loadAmount}
                  onChange={(e) => setLoadAmount(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">Remarks (optional)</label>
                <input
                  data-testid="load-wallet-remarks"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20"
                  placeholder="e.g. UPI top-up ref #123"
                  value={loadRemarks}
                  onChange={(e) => setLoadRemarks(e.target.value)}
                />
              </div>
              <button onClick={submitLoadWallet} disabled={loadingWallet} data-testid="load-wallet-submit" className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                <IndianRupee size={15} /> {loadingWallet ? "Loading…" : "Add to Wallet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Retailer modal */}
      {showAddRetailer && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-[#0D1B2A]">Add New Retailer</h2>
              <button onClick={() => setShowAddRetailer(false)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                ["full_name", "Full Name *", "text", "Retailer's name"],
                ["business_name", "Business Name", "text", "Shop / business name"],
                ["phone", "Mobile Number *", "tel", "10-digit mobile (login id)"],
                ["email", "Email (optional)", "email", "retailer@example.com"],
                ["password", "Password *", "text", "Min 6 characters"],
              ].map(([k, lbl, type, ph]) => (
                <div key={k}>
                  <label className="block text-sm font-semibold text-[#0D1B2A] mb-1.5">{lbl}</label>
                  <input
                    data-testid={`new-retailer-${k}`}
                    type={type}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20"
                    placeholder={ph}
                    value={newRetailer[k]}
                    onChange={(e) => setNewRetailer({ ...newRetailer, [k]: e.target.value })}
                  />
                </div>
              ))}
              <button onClick={createRetailer} disabled={creatingRetailer} data-testid="create-retailer-submit" className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                {creatingRetailer ? "Creating…" : "Create Retailer"}
              </button>
              <p className="text-xs text-[#64748B] text-center">Retailer will log in at /login using their mobile number & password.</p>
            </div>
          </div>
        </div>
      )}

      {/* Signup detail modal */}
      {selectedSignup && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setSelectedSignup(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0D1B2A]">Merchant Application</h2>
              <button onClick={() => setSelectedSignup(null)} className="text-[#64748B]">✕</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Full Name", selectedSignup.full_name], ["Email", selectedSignup.email], ["Phone", selectedSignup.phone],
                ["Business", selectedSignup.business_name], ["Type", selectedSignup.business_type], ["GSTIN", selectedSignup.gstin],
                ["PAN", selectedSignup.pan_number], ["Aadhaar", selectedSignup.aadhaar_number],
                ["Address", selectedSignup.address], ["City", selectedSignup.city], ["State", selectedSignup.state], ["Pincode", selectedSignup.pincode],
                ["Account Holder", selectedSignup.account_holder], ["Bank A/C", selectedSignup.bank_account], ["IFSC", selectedSignup.ifsc],
              ].map(([k, v]) => (
                <div key={k}><div className="text-xs text-[#94A3B8] font-semibold uppercase">{k}</div><div className="text-[#0D1B2A] font-medium">{v || "—"}</div></div>
              ))}
            </div>
            <div className="mt-5">
              <div className="text-xs text-[#94A3B8] font-semibold uppercase mb-2">Documents</div>
              <div className="flex flex-wrap gap-2">
                {(selectedSignup.documents || []).length === 0 && <span className="text-sm text-[#64748B]">No documents</span>}
                {(selectedSignup.documents || []).map((d) => (
                  <a key={d.file_id} href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-3 py-2 rounded-lg">
                    <FileDown size={14} /> {d.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
